// src/AudioSpectrum/effects/vibration.js
import { getHSLColor } from '../colorUtils.js';

export const drawVibration = (ctx, frequencyData, width, height) => {
  // 주파수 데이터 분석
  const bassAvg = frequencyData.slice(0, 20).reduce((a, b) => a + b, 0) / 20;
  const midAvg = frequencyData.slice(20, 100).reduce((a, b) => a + b, 0) / 80;
  const highAvg = frequencyData.slice(100, 200).reduce((a, b) => a + b, 0) / 100;
  const totalAvg = frequencyData.reduce((a, b) => a + b, 0) / frequencyData.length;
  
  // 진동 강도 계산 (0-1 범위)
  const bassIntensity = Math.min(bassAvg / 255, 1);
  const midIntensity = Math.min(midAvg / 255, 1);
  const highIntensity = Math.min(highAvg / 255, 1);
  const totalIntensity = Math.min(totalAvg / 255, 1);
  
  // 진동 계산
  const time = Date.now() * 0.001;
  
  // 베이스 주파수 기반 큰 진동 (저주파 -> 큰 움직임)
  const bassVibrationX = Math.sin(time * 20 + bassIntensity * 50) * bassIntensity * 8;
  const bassVibrationY = Math.cos(time * 18 + bassIntensity * 30) * bassIntensity * 6;
  
  // 중간 주파수 기반 중간 진동
  const midVibrationX = Math.sin(time * 45 + midIntensity * 80) * midIntensity * 4;
  const midVibrationY = Math.cos(time * 42 + midIntensity * 60) * midIntensity * 3;
  
  // 고주파수 기반 세밀한 진동 (고주파 -> 빠른 떨림)
  const highVibrationX = Math.sin(time * 100 + highIntensity * 200) * highIntensity * 2;
  const highVibrationY = Math.cos(time * 95 + highIntensity * 180) * highIntensity * 1.5;
  
  // 총 진동량 합산
  const totalVibrationX = bassVibrationX + midVibrationX + highVibrationX;
  const totalVibrationY = bassVibrationY + midVibrationY + highVibrationY;
  
  // 화면 전체에 진동 적용
  ctx.save();
  ctx.translate(totalVibrationX, totalVibrationY);
  
  // 배경 - 진동 강도에 따른 색상 변화
  const bgIntensity = totalIntensity * 0.3;
  ctx.fillStyle = `rgba(${Math.floor(bgIntensity * 50)}, ${Math.floor(bgIntensity * 30)}, ${Math.floor(bgIntensity * 60)}, 0.1)`;
  ctx.fillRect(-50, -50, width + 100, height + 100);
  
  // 중앙 원형 시각화 - 진동의 중심점
  const centerX = width / 2;
  const centerY = height / 2;
  
  // 메인 원 위의 회색 파티클들 (베이스 반응)
  const mainRadius = 50 + bassIntensity * 100;
  const mainPulse = Math.sin(time * 15) * bassIntensity * 20;
  const finalRadius = mainRadius + mainPulse;
  
  // 원 둘레를 따라 움직이는 파티클들
  const particleCount = 60 + Math.floor(bassIntensity * 40);
  for (let i = 0; i < particleCount; i++) {
    const baseAngle = (i / particleCount) * Math.PI * 2;
    // 파티클들이 원을 따라 흐르는 효과
    const flowSpeed = time * 2 + bassIntensity * 3;
    const angle = baseAngle + flowSpeed;
    
    // 파티클 위치 계산
    const particleX = centerX + Math.cos(angle) * finalRadius;
    const particleY = centerY + Math.sin(angle) * finalRadius;
    
    // 개별 파티클 진동
    const microVibX = Math.sin(time * 30 + i * 0.5) * bassIntensity * 2;
    const microVibY = Math.cos(time * 25 + i * 0.3) * bassIntensity * 1.5;
    
    const finalX = particleX + microVibX;
    const finalY = particleY + microVibY;
    
    // 회색 계열 파티클
    const grayValue = 120 + Math.sin(time * 10 + i * 0.1) * 30 + bassIntensity * 40;
    const alpha = 0.6 + bassIntensity * 0.4;
    const size = 2 + bassIntensity * 3 + Math.sin(time * 20 + i) * 1;
    
    ctx.globalAlpha = alpha;
    ctx.fillStyle = `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
    ctx.beginPath();
    ctx.arc(finalX, finalY, size, 0, Math.PI * 2);
    ctx.fill();
  }
  
  ctx.globalAlpha = 1;
  
  // 중간 주파수 링들
  const midRingCount = 5;
  for (let i = 0; i < midRingCount; i++) {
    const ringRadius = 80 + i * 30 + midIntensity * 50;
    const ringPulse = Math.sin(time * 25 + i * 0.5) * midIntensity * 15;
    const ringOpacity = midIntensity * (1 - i / midRingCount);
    
    ctx.globalAlpha = ringOpacity;
    ctx.strokeStyle = getHSLColor(120 + i * 30, 70, 60);
    ctx.lineWidth = 2 + midIntensity * 3;
    ctx.beginPath();
    ctx.arc(centerX, centerY, ringRadius + ringPulse, 0, Math.PI * 2);
    ctx.stroke();
  }
  
  ctx.globalAlpha = 1;
  
  // 고주파수 기반 세밀한 파티클들
  const particleCount2 = Math.floor(highIntensity * 100) + 20;
  for (let i = 0; i < particleCount2; i++) {
    const angle = (i / particleCount2) * Math.PI * 2 + time * 2;
    const distance = 100 + Math.sin(time * 30 + i * 0.1) * highIntensity * 80;
    
    const particleX = centerX + Math.cos(angle) * distance;
    const particleY = centerY + Math.sin(angle) * distance;
    
    // 고주파수 진동 적용
    const microVibrationX = Math.sin(time * 200 + i) * highIntensity * 3;
    const microVibrationY = Math.cos(time * 180 + i) * highIntensity * 2;
    
    const finalX = particleX + microVibrationX;
    const finalY = particleY + microVibrationY;
    
    const particleSize = 1 + highIntensity * 3;
    const hue = (i * 10 + time * 50) % 360;
    
    ctx.fillStyle = getHSLColor(hue, 80, 60 + highIntensity * 20);
    ctx.beginPath();
    ctx.arc(finalX, finalY, particleSize, 0, Math.PI * 2);
    ctx.fill();
  }
  
  // 노란 나비들이 노니는 모습
  const butterflyCount = 1 + Math.floor(totalIntensity * 20);
  
  for (let i = 0; i < butterflyCount; i++) {
    // 각 나비의 기본 위치와 움직임 패턴
    const butterflyPhase = time * 0.8 + i * 0.7;
    const flightRadius = 150 + Math.sin(time * 0.5 + i) * 80;
    
    // 나비의 기본 궤도 (8자 또는 원형)
    const baseX = centerX + Math.sin(butterflyPhase) * flightRadius;
    const baseY = centerY + Math.cos(butterflyPhase * 0.7) * (flightRadius * 0.6);
    
    // 음악에 반응하는 추가 움직임
    const musicResponseX = Math.sin(time * 10 + i * 2) * totalIntensity * 30;
    const musicResponseY = Math.cos(time * 8 + i * 1.5) * totalIntensity * 20;
    
    // 개별 나비 진동
    const butterflyVibX = Math.sin(time * 40 + i * 3) * totalIntensity * 5;
    const butterflyVibY = Math.cos(time * 35 + i * 2.5) * totalIntensity * 3;
    
    const finalX = baseX + musicResponseX + butterflyVibX;
    const finalY = baseY + musicResponseY + butterflyVibY;
    
    // 경계 확인 (화면 밖으로 나가지 않도록)
    const clampedX = Math.max(20, Math.min(width - 20, finalX));
    const clampedY = Math.max(20, Math.min(height - 20, finalY));
    
    // 나비 날개 애니메이션
    const wingFlap = Math.sin(time * 25 + i * 4) * 0.3 + 0.7; // 0.4 ~ 1.0 범위
    const wingSpread = 8 + totalIntensity * 4; // 음악에 따라 날개 크기 변화
    
    // 나비 방향 (움직임 방향에 따라 회전)
    const dx = Math.sin(butterflyPhase + 0.1) * flightRadius - Math.sin(butterflyPhase) * flightRadius;
    const dy = Math.cos(butterflyPhase * 0.7 + 0.1) * (flightRadius * 0.6) - Math.cos(butterflyPhase * 0.7) * (flightRadius * 0.6);
    const angle = Math.atan2(dy, dx);
    
    ctx.save();
    ctx.translate(clampedX, clampedY);
    ctx.rotate(angle);
    
    // 노란 나비 몸체
    ctx.fillStyle = getHSLColor(45, 70, 40 + totalIntensity * 20);
    ctx.fillRect(-1, -6, 2, 12);
    
    // 왼쪽 날개 (위)
    ctx.fillStyle = getHSLColor(50, 85, 70 + totalIntensity * 15);
    ctx.beginPath();
    ctx.ellipse(-4, -4, wingSpread * wingFlap, wingSpread * 0.6, -0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // 왼쪽 날개 (아래)
    ctx.fillStyle = getHSLColor(48, 80, 65 + totalIntensity * 15);
    ctx.beginPath();
    ctx.ellipse(-3, 2, wingSpread * 0.7 * wingFlap, wingSpread * 0.4, -0.2, 0, Math.PI * 2);
    ctx.fill();
    
    // 오른쪽 날개 (위)
    ctx.fillStyle = getHSLColor(50, 85, 70 + totalIntensity * 15);
    ctx.beginPath();
    ctx.ellipse(4, -4, wingSpread * wingFlap, wingSpread * 0.6, 0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // 오른쪽 날개 (아래)
    ctx.fillStyle = getHSLColor(48, 80, 65 + totalIntensity * 15);
    ctx.beginPath();
    ctx.ellipse(3, 2, wingSpread * 0.7 * wingFlap, wingSpread * 0.4, 0.2, 0, Math.PI * 2);
    ctx.fill();
    
    // 날개 패턴 (작은 점들)
    ctx.fillStyle = getHSLColor(35, 60, 45);
    ctx.beginPath();
    ctx.arc(-3, -3, 1.5, 0, Math.PI * 2);
    ctx.arc(3, -3, 1.5, 0, Math.PI * 2);
    ctx.fill();
    
    // 강한 음악일 때 나비 주변 반짝임
    if (totalIntensity > 0.7) {
      const sparkCount = 3;
      for (let s = 0; s < sparkCount; s++) {
        const sparkAngle = (s / sparkCount) * Math.PI * 2 + time * 5;
        const sparkDist = 15 + Math.sin(time * 20 + s) * 5;
        const sparkX = Math.cos(sparkAngle) * sparkDist;
        const sparkY = Math.sin(sparkAngle) * sparkDist;
        
        ctx.fillStyle = `rgba(255, 255, 100, ${Math.random() * 0.6})`;
        ctx.beginPath();
        ctx.arc(sparkX, sparkY, 1 + Math.random(), 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    ctx.restore();
  }
  
  // 진동 강도 표시 텍스트
  ctx.fillStyle = getHSLColor(0, 0, 80 + totalIntensity * 20);
  ctx.font = `${16 + totalIntensity * 8}px Arial`;
  ctx.textAlign = 'center';
  
  // 텍스트도 진동 효과 적용
  const textVibrationX = Math.sin(time * 50) * totalIntensity * 1;
  const textVibrationY = Math.cos(time * 45) * totalIntensity * 1;
  
//   ctx.fillText(
//     `VIBRATION: ${Math.floor(totalIntensity * 100)}%`,
//     centerX + textVibrationX,
//     50 + textVibrationY
//   );
  
  // 극한 진동시 화면 전체 플래시 효과
  if (totalIntensity > 0.8) {
    const flashIntensity = (totalIntensity - 0.8) * 5;
    ctx.fillStyle = `rgba(255, 255, 255, ${flashIntensity * 0.2 * Math.sin(time * 30)})`;
    ctx.fillRect(-100, -100, width + 200, height + 200);
  }
  
//   // 진동 파형 시각화 (화면 하단)
//   ctx.strokeStyle = getHSLColor(180, 80, 60 + totalIntensity * 20);
//   ctx.lineWidth = 1 + totalIntensity * 3;
//   ctx.beginPath();
  
//   for (let x = 0; x < width; x += 2) {
//     const waveY = height * 0.9 + 
//                   Math.sin((x * 0.02) + time * 10) * bassIntensity * 20 +
//                   Math.sin((x * 0.05) + time * 20) * midIntensity * 10 +
//                   Math.sin((x * 0.1) + time * 40) * highIntensity * 5;
    
//     if (x === 0) {
//       ctx.moveTo(x, waveY);
//     } else {
//       ctx.lineTo(x, waveY);
//     }
//   }
//   ctx.stroke(); 
  
  // 진동 잔향 효과 (이전 프레임의 흔적)
  ctx.globalAlpha = 0.02;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.02)';
  ctx.fillRect(0, 0, width, height);
  ctx.globalAlpha = 1;
};